
import React, { JSX, useEffect, useState } from "react";

import {
    Form,
    Input,
    Select,
    Button,
    Upload,
    Card,
    Row,
    Col,
    Typography,
    message,
    Space,
    Layout,
    Spin,
    Popover,
} from "antd";

import {
    UploadOutlined,
    ReloadOutlined,
    FileAddTwoTone,
    InboxOutlined,
    QuestionCircleOutlined,
    SendOutlined,
    LoadingOutlined,
} from "@ant-design/icons";

import UserNavbar from "../UserNavbar";
import Sider from "antd/es/layout/Sider";
import { Content, Footer } from "antd/es/layout/layout";

import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";

import { createTicket, getCategories } from "./ticketApi";

const { Dragger } = Upload;
const { TextArea } = Input;
const { Title, Text } = Typography;

/*
 * ============================================================
 * ALLOWED FILE TYPES
 * ============================================================
 *
 * Only the following file extensions are allowed:
 *
 * .pdf
 * .xml
 * .text
 * .txt
 * .doc
 *
 * All other file types are rejected.
 */

const ALLOWED_FILE_EXTENSIONS = [
    ".pdf",
    ".xml",
    ".text",
    ".txt",
    ".doc",
    ".docx",
    ".png",
    ".jpg",
    ".jpeg",
    ".xls",
    ".xlsx",
    ".zip"
];
const ALLOWED_FILE_ACCEPT =
    ALLOWED_FILE_EXTENSIONS.join(",");

/*
 * ============================================================
 * FILE LIMITS
 * ============================================================
 */

const MAX_FILE_SIZE = 500 * 1024; // 500 KB
const MAX_FILES = 2;

/*
 * ============================================================
 * DANGEROUS FILE EXTENSIONS
 * ============================================================
 *
 * These are explicitly rejected even when they appear before
 * the final extension.
 *
 * Example:
 *
 * malicious.html.pdf
 * test.js.pdf
 * payload.svg.pdf
 *
 * These files are rejected.
 */

const DANGEROUS_FILE_EXTENSIONS = [
    ".html",
    ".htm",
    ".xhtml",
    ".xss",
    ".js",
    ".jsx",
    ".mjs",
    ".ts",
    ".tsx",
    ".exe",
    ".dll",
    ".bat",
    ".cmd",
    ".com",
    ".scr",
    ".sh",
    ".bash",
    ".ps1",
    ".php",
    ".php3",
    ".php4",
    ".php5",
    ".phtml",
    ".jsp",
    ".jspx",
    ".asp",
    ".aspx",
    ".cgi",
    ".pl",
    ".py",
    ".rb",
    ".svg",
];

/*
 * ============================================================
 * INPUT VALIDATION REGEX
 * ============================================================
 */

const TITLE_REGEX = /^[A-Za-z0-9\s\-\/]+$/;

const DESCRIPTION_REGEX =
    /^[A-Za-z0-9\s\-\/()]+$/;

/*
 * ============================================================
 * TICKET GUIDELINES
 * ============================================================
 */

const guidelineContent = (
    <Space direction="vertical" size={10}>
        <Text>
            ✔ Provide a clear and meaningful ticket title.
        </Text>

        <Text>
            ✔ Describe the issue with complete steps to reproduce.
        </Text>

        <Text>
            ✔ Select the appropriate Category and Ticket Type.
        </Text>

        <Text>
            ✔ Mention the Error Code whenever applicable.
        </Text>

        <Text>
            ✔ Attach supporting documents only if relevant.
        </Text>

        <Text>
            ✔ Maximum 2 supporting documents are allowed.
        </Text>

        <Text>
            ✔ Each attachment must not exceed 500 KB.
        </Text>

        <Text>
            ✔ Supported file types:
            PDF, XML, TEXT, TXT and DOC.
        </Text>
    </Space>
);

/*
 * ============================================================
 * FORM INTERFACE
 * ============================================================
 */

interface TicketForm {
    title: string;
    customer: string;
    category: string;
    environment: string;
    ticketType: string;
    errorCode?: string;
    description: string;
}

/*
 * ============================================================
 * LABEL STYLE
 * ============================================================
 */

const labelStyle = {
    fontWeight: "bold",
    fontSize: "16px",
};

/*
 * ============================================================
 * TICKET TYPE OPTIONS
 * ============================================================
 */

const ticketTypeOptions = {
    TECH_SUPPORT: [
        {
            label: "Technical",
            value: "TECHNICAL",
        },
    ],

    ONBOARDING: [
        {
            label: "Management",
            value: "MANAGEMENT",
        },
    ],
};

/*
 * ============================================================
 * MAIN COMPONENT
 * ============================================================
 */

function NewTicket(): JSX.Element {
    const [form] = Form.useForm();

    const selectedCategory =
        Form.useWatch("category", form);

    const [isOnClickSlider, setOnclickSlider] =
        useState(false);

    const [loading, setLoading] =
        useState(false);

    const [isMobile, setIsMobile] =
        useState(window.innerWidth < 830);

    const [fileList, setFileList] =
        useState<any[]>([]);

    const [ticketTypes, setTicketTypes] =
        useState<any[]>([]);

    const userfromstore = useSelector(
        (state: RootState) => state.user.user
    );

    const [categories, setCategories] =
        useState<any[]>([]);

    /*
     * ========================================================
     * TITLE VALIDATOR
     * ========================================================
     */

    const validateTitle = (
        _: any,
        value: string
    ) => {
        if (!value || !value.trim()) {
            return Promise.reject(
                new Error("Please enter ticket title")
            );
        }

        const trimmedValue = value.trim();

        if (!TITLE_REGEX.test(trimmedValue)) {
            return Promise.reject(
                new Error(
                    "Title can contain only letters, numbers, spaces, '-' and '/'"
                )
            );
        }

        return Promise.resolve();
    };

    /*
     * ========================================================
     * DESCRIPTION VALIDATOR
     * ========================================================
     */

    const validateDescription = (
        _: any,
        value: string
    ) => {
        if (!value || !value.trim()) {
            return Promise.reject(
                new Error("Please enter description")
            );
        }

        const trimmedValue = value.trim();

        if (trimmedValue.length < 20) {
            return Promise.reject(
                new Error(
                    "Minimum 20 characters required"
                )
            );
        }

        if (!DESCRIPTION_REGEX.test(trimmedValue)) {
            return Promise.reject(
                new Error(
                    "Description can contain only letters, numbers, spaces, '-', '/', '(' and ')'"
                )
            );
        }

        return Promise.resolve();
    };

    /*
     * ========================================================
     * FILE VALIDATION
     * ========================================================
     *
     * Client-side validation:
     *
     * 1. Filename must exist
     * 2. Extension must be allowed
     * 3. Dangerous extensions are rejected
     * 4. Filename characters are validated
     * 5. File size is validated
     *
     * NOTE:
     * This is client-side validation only.
     * Backend validation is mandatory for security.
     */

    const validateFile = (
        file: File
    ): boolean => {

        if (!file) {
            message.error(
                "Invalid file."
            );

            return false;
        }

        const originalFileName =
            file.name || "";

        const fileName =
            originalFileName
                .trim()
                .toLowerCase();

        /*
         * ----------------------------------------------------
         * Validate filename
         * ----------------------------------------------------
         */

        if (!fileName) {
            message.error(
                "File name is required."
            );

            return false;
        }

        /*
         * Reject path separators and control characters.
         *
         * This prevents filenames such as:
         *
         * ../../test.pdf
         * ..\\test.pdf
         */

        const dangerousFilenameCharacters =
            /[<>:"/\\|?*\x00-\x1F]/;

        if (
            dangerousFilenameCharacters.test(
                originalFileName
            )
        ) {
            message.error(
                `${originalFileName}: Invalid file name.`
            );

            return false;
        }

        /*
         * ----------------------------------------------------
         * Get final extension
         * ----------------------------------------------------
         */

        const lastDotIndex =
            fileName.lastIndexOf(".");

        if (
            lastDotIndex === -1 ||
            lastDotIndex === fileName.length - 1
        ) {
            message.error(
                `${originalFileName}: File extension is required.`
            );

            return false;
        }

        const extension =
            fileName.substring(
                lastDotIndex
            );

        /*
         * ----------------------------------------------------
         * Check final extension against allowlist
         * ----------------------------------------------------
         */

        if (
            !ALLOWED_FILE_EXTENSIONS.includes(
                extension
            )
        ) {
            message.error(
                `${originalFileName}: Invalid file type. Allowed files are PDF, XML, TEXT, TXT and DOC only.`
            );

            return false;
        }

        /*
         * ----------------------------------------------------
         * Check for dangerous extensions anywhere before
         * the final extension.
         *
         * Examples rejected:
         *
         * test.html.pdf
         * test.js.pdf
         * test.svg.pdf
         * test.jsp.pdf
         * ----------------------------------------------------
         */

        const fileNameWithoutFinalExtension =
            fileName.substring(
                0,
                lastDotIndex
            );

        const containsDangerousExtension =
            DANGEROUS_FILE_EXTENSIONS.some(
                (dangerousExtension) =>
                    fileNameWithoutFinalExtension.includes(
                        dangerousExtension
                    )
            );

        if (
            containsDangerousExtension
        ) {
            message.error(
                `${originalFileName}: Potentially unsafe file name.`
            );

            return false;
        }

        /*
         * ----------------------------------------------------
         * Prevent multiple extensions with suspicious patterns.
         *
         * Example:
         *
         * test.something.pdf
         *
         * Only allow simple filenames for attachments.
         * ----------------------------------------------------
         */

        const filenameWithoutExtension =
            fileName.substring(
                0,
                lastDotIndex
            );

        if (
            filenameWithoutExtension.includes(".")
        ) {
            message.error(
                `${originalFileName}: Multiple file extensions are not allowed.`
            );

            return false;
        }

        /*
         * ----------------------------------------------------
         * File size validation
         * ----------------------------------------------------
         */

        if (
            file.size > MAX_FILE_SIZE
        ) {
            message.error(
                `${originalFileName} exceeds the maximum allowed size of 500 KB.`
            );

            return false;
        }

        /*
         * ----------------------------------------------------
         * Zero-byte file validation
         * ----------------------------------------------------
         */

        if (file.size === 0) {
            message.error(
                `${originalFileName}: Empty files are not allowed.`
            );

            return false;
        }

        return true;
    };

    /*
     * ========================================================
     * BEFORE UPLOAD
     * ========================================================
     */

    const beforeUpload = (
        file: File
    ) => {

        /*
         * Maximum file count
         */

        if (fileList.length >= MAX_FILES) {
            message.error(
                "You can upload a maximum of 2 supporting documents."
            );

            return Upload.LIST_IGNORE;
        }

        /*
         * Validate file
         */

        if (!validateFile(file)) {
            return Upload.LIST_IGNORE;
        }

        /*
         * Returning false prevents Ant Design from
         * automatically uploading the file.
         */

        return false;
    };

    /*
     * ========================================================
     * HANDLE FILE CHANGE
     * ========================================================
     */

    const handleFileChange = ({
        fileList: changedFileList,
    }: {
        fileList: any[];
    }) => {

        const validFiles =
            changedFileList.filter(
                (file) => {

                    if (
                        !file.originFileObj
                    ) {
                        return false;
                    }

                    return validateFile(
                        file.originFileObj
                    );
                }
            );

        setFileList(
            validFiles.slice(
                0,
                MAX_FILES
            )
        );
    };

    /*
     * ========================================================
     * HANDLE SUBMIT
     * ========================================================
     */

    const handleSubmit = async (
        values: TicketForm
    ) => {

        console.log(
            "Ticket Data:",
            values
        );

        console.log(
            "File Data:",
            fileList
        );

        if (loading) {
            return;
        }

        /*
         * Final client-side file validation before API call.
         */

        for (const file of fileList) {

            if (
                !file.originFileObj
            ) {
                message.error(
                    "Invalid attachment."
                );

                return;
            }

            if (
                !validateFile(
                    file.originFileObj
                )
            ) {
                return;
            }
        }

        setLoading(true);

        try {

            const response =
                await createTicket(
                    values,
                    fileList,
                    userfromstore?.username
                );

            console.log(response);

            message.success(
                `Ticket created successfully with Ticket ID: ${response}`
            );

            form.resetFields();

            setFileList([]);

        } catch (error) {

            console.error(error);

            message.error(
                "Unable to create ticket"
            );

        } finally {

            setLoading(false);
        }
    };

    /*
     * ========================================================
     * SET ORGANIZATION NAME
     * ========================================================
     */

    useEffect(() => {

        if (!userfromstore) {
            return;
        }

        form.setFieldsValue({
            organizationName:
                userfromstore.organizationName,
        });

    }, [
        userfromstore,
        form,
    ]);

    /*
     * ========================================================
     * WINDOW RESIZE
     * ========================================================
     */

    useEffect(() => {

        const handleResize = () => {

            setIsMobile(
                window.innerWidth < 830
            );
        };

        window.addEventListener(
            "resize",
            handleResize
        );

        return () =>
            window.removeEventListener(
                "resize",
                handleResize
            );

    }, []);

    /*
     * ========================================================
     * LOAD CATEGORIES
     * ========================================================
     */

    useEffect(() => {

        const loadCategories =
            async () => {

                try {

                    const data =
                        await getCategories();

                    setCategories(data);

                } catch (error) {

                    console.error(
                        "Unable to load categories",
                        error
                    );

                    message.error(
                        "Unable to load ticket categories"
                    );
                }
            };

        loadCategories();

    }, []);

    /*
     * ========================================================
     * RENDER
     * ========================================================
     */

    return (
        <>
            {loading && (
                <Spin
                    spinning={true}
                    style={{
                        position:
                            "absolute",
                        top: "50%",
                        left: "50%",
                        zIndex: 9999,
                    }}
                    indicator={
                        <LoadingOutlined
                            style={{
                                fontSize: 48,
                            }}
                            spin
                        />
                    }
                />
            )}

            <Layout
                style={{
                    minHeight: "89vh",
                }}
            >

                <Sider
                    collapsed={
                        isMobile
                            ? true
                            : isOnClickSlider
                    }
                    collapsedWidth={50}
                    style={{
                        background:
                            "white",
                        position:
                            "relative",
                        overflow: "auto",
                        top: "0px",
                        paddingTop:
                            "20px",
                    }}
                >

                    <div
                        style={{
                            top: "0",
                            position:
                                "sticky",
                            height: "100%",
                        }}
                    >
                        <UserNavbar />
                    </div>

                </Sider>

                <Layout>

                    <Content
                        style={{
                            margin:
                                "16px 16px 0",
                        }}
                    >

                        <div>

                            <div
                                style={{
                                    padding:
                                        "6px",
                                }}
                            >

                                {/* PAGE HEADER */}

                                <div
                                    style={{
                                        display:
                                            "flex",
                                        justifyContent:
                                            "space-between",
                                        alignItems:
                                            "center",
                                        marginBottom:
                                            24,
                                        flexWrap:
                                            "wrap",
                                    }}
                                >

                                    <Space align="start">

                                        <FileAddTwoTone
                                            style={{
                                                fontSize:
                                                    38,
                                            }}
                                        />

                                        <div>

                                            <Title
                                                level={3}
                                                style={{
                                                    margin:
                                                        0,
                                                }}
                                            >
                                                Create New Ticket
                                            </Title>

                                            <Text type="secondary">
                                                Submit a support request and track its status.
                                            </Text>

                                        </div>

                                    </Space>

                                    <Popover
                                        title="Ticket Guidelines"
                                        content={
                                            guidelineContent
                                        }
                                        trigger="click"
                                        placement="bottomRight"
                                    >

                                        <Button
                                            type="link"
                                            icon={
                                                <QuestionCircleOutlined />
                                            }
                                        >
                                            Ticket Guidelines
                                        </Button>

                                    </Popover>

                                </div>

                                {/* FORM CARD */}

                                <Card
                                    style={{
                                        background:
                                            "#f2f4ff",
                                        marginBottom:
                                            20,
                                    }}
                                    bordered={false}
                                >

                                    <Form
                                        form={form}
                                        layout="vertical"
                                        onFinish={
                                            handleSubmit
                                        }
                                    >

                                        {/* FIRST ROW */}

                                        <Row
                                            gutter={[
                                                24,
                                                0,
                                            ]}
                                        >

                                            {/* TITLE */}

                                            <Col
                                                xs={24}
                                                md={12}
                                            >

                                                <Form.Item
                                                    label={
                                                        <span
                                                            style={
                                                                labelStyle
                                                            }
                                                        >
                                                            Title
                                                        </span>
                                                    }
                                                    name="title"
                                                    rules={[
                                                        {
                                                            validator:
                                                                validateTitle,
                                                        },
                                                        {
                                                            max: 200,
                                                            message:
                                                                "Maximum 200 characters allowed",
                                                        },
                                                    ]}
                                                >

                                                    <Input
                                                        placeholder="Enter ticket title"
                                                        maxLength={
                                                            200
                                                        }
                                                        onChange={(
                                                            e
                                                        ) => {

                                                            const value =
                                                                e
                                                                    .target
                                                                    .value;

                                                            const sanitized =
                                                                value.replace(
                                                                    /[^A-Za-z0-9\s\-\/]/g,
                                                                    ""
                                                                );

                                                            if (
                                                                sanitized !==
                                                                value
                                                            ) {

                                                                form.setFieldsValue(
                                                                    {
                                                                        title:
                                                                            sanitized,
                                                                    }
                                                                );
                                                            }

                                                        }}
                                                    />

                                                </Form.Item>

                                            </Col>

                                            {/* ORGANIZATION */}

                                            <Col
                                                xs={24}
                                                md={12}
                                            >

                                                <Form.Item
                                                    label={
                                                        <span
                                                            style={
                                                                labelStyle
                                                            }
                                                        >
                                                            Organization/Company Name
                                                        </span>
                                                    }
                                                    name="organizationName"
                                                >

                                                    <Input
                                                        readOnly
                                                        bordered={false}
                                                        style={{
                                                            background:
                                                                "#f5f5f5",
                                                            cursor:
                                                                "default",
                                                            color:
                                                                "#000",
                                                            fontWeight:
                                                                500,
                                                        }}
                                                    />

                                                </Form.Item>

                                            </Col>

                                            {/* CATEGORY */}

                                            <Col
                                                xs={24}
                                                md={8}
                                            >

                                                <Form.Item
                                                    label={
                                                        <span
                                                            style={
                                                                labelStyle
                                                            }
                                                        >
                                                            Category
                                                        </span>
                                                    }
                                                    name="category"
                                                    rules={[
                                                        {
                                                            required:
                                                                true,
                                                            message:
                                                                "Please select category",
                                                        },
                                                    ]}
                                                >

                                                    <Select
                                                        placeholder="Select Category"
                                                        options={categories.map(
                                                            (
                                                                category: any
                                                            ) => ({
                                                                label:
                                                                    category.categoryName,
                                                                value:
                                                                    category.categoryName,
                                                            })
                                                        )}
                                                        onChange={(
                                                            value
                                                        ) => {

                                                            if (
                                                                value !==
                                                                "Technical"
                                                            ) {

                                                                form.setFieldsValue(
                                                                    {
                                                                        environment:
                                                                            undefined,
                                                                    }
                                                                );
                                                            }

                                                        }}
                                                    />

                                                </Form.Item>

                                            </Col>

                                            {/* ENVIRONMENT */}

                                            <Col
                                                xs={24}
                                                md={8}
                                            >

                                                <Form.Item
                                                    label={
                                                        <span
                                                            style={
                                                                labelStyle
                                                            }
                                                        >
                                                            Environment
                                                        </span>
                                                    }
                                                    name="environment"
                                                    dependencies={[
                                                        "category",
                                                    ]}
                                                    rules={[
                                                        {
                                                            required:
                                                                selectedCategory ===
                                                                "Technical",
                                                            message:
                                                                "Please select environment",
                                                        },
                                                    ]}
                                                >

                                                    <Select
                                                        placeholder={
                                                            selectedCategory ===
                                                            "Technical"
                                                                ? "Select Environment"
                                                                : "Environment not applicable"
                                                        }
                                                        disabled={
                                                            selectedCategory !==
                                                            "Technical"
                                                        }
                                                        options={[
                                                            {
                                                                label:
                                                                    "Staging",
                                                                value:
                                                                    "STAGING",
                                                            },
                                                            {
                                                                label:
                                                                    "Pre-Production(Primary)",
                                                                value:
                                                                    "PRE_PRODUCTION(Primary)",
                                                            },
                                                            {
                                                                label:
                                                                    "Production(Primary)",
                                                                value:
                                                                    "PRODUCTION(Primary)",
                                                            },
                                                            {
                                                                label:
                                                                    "Pre-Production(Secondary)",
                                                                value:
                                                                    "PRE_PRODUCTION(Secondary)",
                                                            },
                                                            {
                                                                label:
                                                                    "Production(Secondary)",
                                                                value:
                                                                    "PRODUCTION(Secondary)",
                                                            },
                                                        ]}
                                                    />

                                                </Form.Item>

                                            </Col>

                                            {/* ERROR CODE */}

                                            <Col
                                                xs={24}
                                                md={8}
                                            >

                                                <Form.Item
                                                    label={
                                                        <span
                                                            style={
                                                                labelStyle
                                                            }
                                                        >
                                                            Error Code(Optional)
                                                        </span>
                                                    }
                                                    name="errorCode"
                                                >

                                                    <Input
                                                        placeholder="Enter error code (if any)"
                                                    />

                                                </Form.Item>

                                            </Col>

                                        </Row>

                                        {/* SECOND ROW */}

                                        <Row
                                            gutter={[
                                                24,
                                                0,
                                            ]}
                                        >

                                            {/* DESCRIPTION */}

                                            <Col
                                                xs={24}
                                                md={16}
                                            >

                                                <Form.Item
                                                    label={
                                                        <span
                                                            style={
                                                                labelStyle
                                                            }
                                                        >
                                                            Description
                                                        </span>
                                                    }
                                                    name="description"
                                                    rules={[
                                                        {
                                                            validator:
                                                                validateDescription,
                                                        },
                                                    ]}
                                                >

                                                    <TextArea
                                                        rows={6}
                                                        maxLength={
                                                            5000
                                                        }
                                                        showCount
                                                        placeholder="Describe the issue in detail. Use letters, numbers, spaces, -, /, ( and ) only."
                                                        onChange={(
                                                            e
                                                        ) => {

                                                            const value =
                                                                e
                                                                    .target
                                                                    .value;

                                                            const sanitized =
                                                                value.replace(
                                                                    /[^A-Za-z0-9\s\-\/()]/g,
                                                                    ""
                                                                );

                                                            if (
                                                                sanitized !==
                                                                value
                                                            ) {

                                                                form.setFieldsValue(
                                                                    {
                                                                        description:
                                                                            sanitized,
                                                                    }
                                                                );
                                                            }

                                                        }}
                                                    />

                                                </Form.Item>

                                            </Col>

                                            {/* ATTACHMENTS */}

                                            <Col
                                                xs={24}
                                                md={8}
                                            >

                                                <Form.Item
                                                    label={
                                                        <span
                                                            style={
                                                                labelStyle
                                                            }
                                                        >
                                                            Supporting Documents
                                                        </span>
                                                    }
                                                    extra={
                                                        "Maximum 2 documents • Maximum file size: 500 KB each • Allowed: PDF, XML, TEXT, TXT, DOC"
                                                    }
                                                >

                                                    <Dragger
                                                        name="files"
                                                        multiple
                                                        beforeUpload={
                                                            beforeUpload
                                                        }
                                                        fileList={
                                                            fileList
                                                        }
                                                        maxCount={
                                                            MAX_FILES
                                                        }
                                                        accept={
                                                            ALLOWED_FILE_ACCEPT
                                                        }
                                                        onChange={
                                                            handleFileChange
                                                        }
                                                        style={{
                                                            borderRadius:
                                                                10,
                                                            padding:
                                                                "12px 8px",
                                                            minHeight:
                                                                90,
                                                        }}
                                                    >

                                                        <InboxOutlined
                                                            style={{
                                                                fontSize:
                                                                    30,
                                                                color:
                                                                    "#1677ff",
                                                                marginBottom:
                                                                    8,
                                                            }}
                                                        />

                                                        <div
                                                            style={{
                                                                fontWeight:
                                                                    500,
                                                            }}
                                                        >
                                                            Drop files here
                                                        </div>

                                                        <Text type="secondary">
                                                            or Browse Files
                                                        </Text>

                                                        <div
                                                            style={{
                                                                marginTop:
                                                                    8,
                                                                fontSize:
                                                                    12,
                                                            }}
                                                        >
                                                            PDF, XML, TEXT, TXT, DOC
                                                        </div>

                                                    </Dragger>

                                                </Form.Item>

                                            </Col>

                                        </Row>

                                        {/* BUTTONS */}

                                        <Row
                                            justify="end"
                                            gutter={[
                                                24,
                                                0,
                                            ]}
                                            style={{
                                                justifyContent:
                                                    "center",
                                            }}
                                        >

                                            <Col
                                                xs={12}
                                                md={12}
                                            >

                                                <Button
                                                    style={{
                                                        width:
                                                            "100%",
                                                    }}
                                                    icon={
                                                        <ReloadOutlined />
                                                    }
                                                    onClick={() => {

                                                        form.resetFields();

                                                        setFileList(
                                                            []
                                                        );

                                                    }}
                                                >
                                                    Reset
                                                </Button>

                                            </Col>

                                            <Col
                                                xs={12}
                                                md={12}
                                            >

                                                <Button
                                                    style={{
                                                        width:
                                                            "100%",
                                                    }}
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={
                                                        loading
                                                    }
                                                    disabled={
                                                        loading
                                                    }
                                                    icon={
                                                        <span
                                                            style={{
                                                                display:
                                                                    "inline-block",
                                                                transform:
                                                                    "rotate(-45deg)",
                                                            }}
                                                        >
                                                            <SendOutlined />
                                                        </span>
                                                    }
                                                >
                                                    {loading
                                                        ? "Submitting Ticket..."
                                                        : "Submit Ticket"}
                                                </Button>

                                            </Col>

                                        </Row>

                                    </Form>

                                </Card>

                            </div>

                        </div>

                    </Content>

                    <Footer
                        style={{
                            textAlign:
                                "center",
                        }}
                    >
                        ©2025 Copyright: C-DAC Mumbai.
                        All Rights Reserved
                    </Footer>

                </Layout>

            </Layout>
        </>
    );
}

export default NewTicket;

